import React, { useCallback, useEffect, useState } from "react";

import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useIntl } from "react-intl";

import { Company } from "@mapmycustomers/shared/types/entity";
import { FormItem } from "@mapmycustomers/ui";

import ButtonLink from "@app/component/ButtonLink";
import AddressFormItem from "@app/component/createEditEntity/AddressFormItem";
import { useAddressFieldValidationRules } from "@app/util/validation/address-validation";

import { inheritAddressFromCompany } from "../utils/inheritAddressFromCompany";

import styles from "./AddressField.module.scss";

interface Props {
  disabled?: boolean;
  inheritAddressFrom?: Company;
  required?: boolean;
}

const AddressField = ({ disabled, inheritAddressFrom, required }: Props) => {
  const intl = useIntl();
  const form = useFormInstance();
  const [autoInheritedAddress, setAutoInheritedAddress] = useState<boolean>(false);

  const addressFieldValidation = useAddressFieldValidationRules(intl, form);

  useEffect(() => {
    if (inheritAddressFrom) {
      inheritAddressFromCompany(form, inheritAddressFrom);
      setAutoInheritedAddress(true);
    }
  }, [form, inheritAddressFrom]);

  const handleClearClick = useCallback(() => {
    form.setFieldsValue({
      addressDetails: undefined,
      latitude: undefined,
      longitude: undefined,
    });
    setAutoInheritedAddress(false);
  }, [form]);

  const handleInheritClick = useCallback(() => {
    if (inheritAddressFrom) {
      inheritAddressFromCompany(form, inheritAddressFrom);
      setAutoInheritedAddress(true);
    }
  }, [form, inheritAddressFrom]);

  return (
    <>
      <AddressFormItem
        locked={disabled}
        required={required}
        rules={required ? addressFieldValidation : undefined}
      />
      <FormItem dependencies={["accounts"]} noStyle>
        {() => {
          if (!inheritAddressFrom?.geoPoint || disabled) {
            return null;
          }
          return (
            <div className={styles.inheritAddress}>
              {autoInheritedAddress ? (
                <div className={styles.autoFilledAddressAlert}>
                  {intl.formatMessage(
                    {
                      id: "formFields.addressField.autoInheritedAddress",
                      defaultMessage: "Location auto-filled from {companyName}",
                      description: "inherited location by default",
                    },
                    { companyName: inheritAddressFrom.name }
                  )}
                  <ButtonLink onClick={handleClearClick}>
                    {intl.formatMessage({
                      id: "formFields.addressField.clearAddress",
                      defaultMessage: "Clear",
                      description: "clear address from address field",
                    })}
                  </ButtonLink>
                </div>
              ) : (
                <div className={styles.autoFill}>
                  <ButtonLink onClick={handleInheritClick}>
                    {intl.formatMessage(
                      {
                        id: "formFields.addressField.inheritFromCompany",
                        defaultMessage: "Auto-fill address from {companyName}",
                        description:
                          "Inherit location from Company button text on the Create Person modal",
                      },
                      { companyName: inheritAddressFrom.name }
                    )}
                  </ButtonLink>
                </div>
              )}
            </div>
          );
        }}
      </FormItem>
    </>
  );
};

export default AddressField;
